import React, { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import AnalyticsChartsGrid from '../charts/AnalyticsChartsGrid';
import TopPerformersSection from '../tables/TopPerformersSection';
import {
  usePublicCampaignBrand,
  usePublicCampaignOrganization,
  usePublicCampaignMetadata,
  usePublicCampaignBranding,
  usePublicCampaignPerformanceTimeSeries,
  usePublicCampaignSummary,
  usePublicDeliverableAggregates,
  usePublicPlatformDistribution,
  usePublicTopPerformingPosts,
} from '../../hooks/usePublicAnalytics';
import { useSharedAnalyticsCalculations } from '../../hooks/useSharedAnalyticsCalculations';
import { useCurrentOrganization } from '@/domains/organizations/hooks/useCurrentOrganization';
import PublicPageState from './PublicPageState';
import SharedAnalyticsHeader from './SharedAnalyticsHeader';
import SharedAnalyticsFooter from './SharedAnalyticsFooter';
import SharedAnalyticsMetrics from './SharedAnalyticsMetrics';

export const SharedAnalyticsPage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();

  // Ensure referrer is never leaked from the public share page
  useEffect(() => {
    let meta = document.querySelector('meta[name="referrer"]') as HTMLMetaElement | null;
    let created = false;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'referrer';
      meta.content = 'no-referrer';
      document.head.appendChild(meta);
      created = true;
    } else {
      meta.content = 'no-referrer';
    }

    return () => {
      if (created && meta && meta.parentNode) {
        meta.parentNode.removeChild(meta);
      }
    };
  }, []);

  const { data: currentOrganization } = useCurrentOrganization();
  const metadataQuery = usePublicCampaignMetadata(token);
  const brandingQuery = usePublicCampaignBranding(token);
  const brandQuery = usePublicCampaignBrand(metadataQuery.data?.campaign_id);
  const orgQuery = usePublicCampaignOrganization(
    metadataQuery.data?.organization_id,
    metadataQuery.data?.campaign_id
  );
  const aggregatesQuery = usePublicDeliverableAggregates(token);
  const summaryQuery = usePublicCampaignSummary(token);
  const performanceQuery = usePublicCampaignPerformanceTimeSeries(token);
  const platformQuery = usePublicPlatformDistribution(token);
  const postsQuery = usePublicTopPerformingPosts(token);

  const [showAllInfluencers, setShowAllInfluencers] = useState(false);
  const [showAllPosts, setShowAllPosts] = useState(false);

  const aggregates = useMemo(() => aggregatesQuery.data ?? [], [aggregatesQuery.data]);
  const allPosts = useMemo(() => postsQuery.data ?? [], [postsQuery.data]);
  const campaignPerformance = useMemo(() => performanceQuery.data ?? [], [performanceQuery.data]);
  const platformDistribution = useMemo(() => platformQuery.data ?? [], [platformQuery.data]);

  const { metrics, topInfluencers, topPosts } =
    useSharedAnalyticsCalculations({
      aggregates,
      summaryData: summaryQuery.data,
      posts: allPosts,
      showAllInfluencers,
      showAllPosts,
    });

  const totalViews = useMemo(
    () => aggregates.reduce((s, d) => s + (d.total_views || 0), 0),
    [aggregates]
  );

  if (!token) {
    return (
      <PublicPageState
        title="Missing share token"
        description="This analytics link is incomplete."
      />
    );
  }

  if (metadataQuery.isLoading) {
    return (
      <LoadingSpinner
        size="fullscreen"
        label="Loading shared analytics..."
        className="bg-gray-50 min-h-screen"
      />
    );
  }

  if (metadataQuery.isError || !metadataQuery.data) {
    return (
      <PublicPageState
        title="Link expired or invalid"
        description="This public analytics link may have expired, been revoked, or been copied incorrectly."
      />
    );
  }

  const hasDataError =
    aggregatesQuery.isError ||
    summaryQuery.isError ||
    performanceQuery.isError ||
    platformQuery.isError ||
    postsQuery.isError;

  const isBrand =
    currentOrganization?.organization_type === 'brand' ||
    orgQuery.data?.organization_type === 'brand';

  const currentOrgSettings =
    currentOrganization?.settings && typeof currentOrganization.settings === 'object'
      ? (currentOrganization.settings as Record<string, unknown>)
      : undefined;

  const organizationName =
    brandingQuery.data?.organization_name ??
    orgQuery.data?.name ??
    currentOrganization?.name;

  const organizationLogo =
    brandingQuery.data?.organization_logo_url ??
    orgQuery.data?.logo_url ??
    (currentOrgSettings?.logo_url as string | undefined) ??
    ((currentOrganization as Record<string, unknown> | undefined)?.logo_url as string | undefined);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Public Header */}
      <SharedAnalyticsHeader
        campaignName={metadataQuery.data.campaign_name}
        brandName={brandingQuery.data?.brand_name ?? brandQuery.data?.name}
        brandLogo={brandingQuery.data?.brand_logo_url ?? brandQuery.data?.logo_url}
        organizationName={organizationName}
        organizationLogo={organizationLogo}
        isBrand={isBrand}
      />

      <main className="mx-auto max-w-[92%] space-y-6 px-6 py-6">
        {hasDataError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            Some analytics data could not be loaded. Please refresh the page or try again later.
          </div>
        )}

        {/* Metric Cards */}
        <SharedAnalyticsMetrics metrics={metrics} />

        {/* Charts: Performance, Distribution & Trend */}
        <AnalyticsChartsGrid
          campaignPerformance={campaignPerformance}
          performanceLoading={performanceQuery.isLoading}
          performanceError={performanceQuery.isError}
          onRetryPerformance={() => void performanceQuery.refetch()}
          platformDistribution={platformDistribution}
          platformLoading={platformQuery.isLoading}
          platformError={platformQuery.isError}
          onRetryPlatform={() => void platformQuery.refetch()}
          totalViews={totalViews}
        />

        {/* Top Performers (Influencers & Posts) */}
        <TopPerformersSection
          topInfluencers={topInfluencers}
          aggregatesLoading={aggregatesQuery.isLoading}
          onToggleShowAllInfluencers={() => setShowAllInfluencers((v) => !v)}
          topPosts={topPosts}
          postsLoading={postsQuery.isLoading}
          onToggleShowAllPosts={() => setShowAllPosts((v) => !v)}
        />
      </main>

      {/* Footer */}
      <SharedAnalyticsFooter />
    </div>
  );
};

export default SharedAnalyticsPage;
